import type { NativeF0Frame, PitchAnalysisConfig } from '@muzly/shared';
import { autoCorrelate, clampNum, freqToNote } from '@muzly/voice-engine';

/**
 * micFrames.ts — 브라우저 마이크 창 하나 → Analysis Frame. **순수 함수**다(테스트 대상).
 * 마이크·AudioContext 같은 IO 절반은 `browserSource.ts`가 갖는다(admin 엔진 어댑터와
 * 같은 분리: DSP는 순수 쪽에, 브라우저 전용 IO는 반대쪽에).
 *
 * **이건 엔진이 아니다.** 추정기는 `@muzly/voice-engine`의 JS ACF이고 네이티브 MPM과
 * 다른 알고리즘이다 — 같은 녹음 15개에서 클립 중앙 f0가 5개는 100센트 이상, 2개는 한
 * 옥타브 넘게 엇갈렸다(2026-07-29 실측). 그래서 프레임은 `algorithm: 'acf'`로 자기를
 * 밝히고, 화면도 "엔진 아님"을 표시해야 한다. 존재 이유는 하나다: 브라우저 mock은
 * confidence 0.95 · rms −18dBFS · 고정 음높이를 내보내므로 **어떤 튜닝도 눈에 보이지
 * 않는다.** 실제 목소리가 들어와야 임계값에 걸릴 것이 생긴다.
 */

/** dBFS. 완전 무음(rms 0)은 log가 -Inf라 바닥값으로 눌러 리드아웃이 깨지지 않게 한다. */
const FLOOR_DB = -120;

export const windowRmsDb = (window: Float32Array): number => {
  if (window.length === 0) {
    return FLOOR_DB;
  }
  let sum = 0;
  for (let i = 0; i < window.length; i += 1) {
    sum += window[i] * window[i];
  }
  const rms = Math.sqrt(sum / window.length);
  return rms > 0 ? Math.max(FLOOR_DB, 20 * Math.log10(rms)) : FLOOR_DB;
};

/**
 * 고른 lag에서의 **정규화 상관값**(0~1) — 이 경로의 confidence다.
 *
 * `autoCorrelate`는 Hz만 돌려주고 신뢰도를 내지 않는다. 그렇다고 상수 1을 박으면
 * `confidence_threshold` 슬라이더가 브라우저에서 아무 일도 안 하는 또 하나의 죽은
 * 노브가 된다. 그래서 주기성을 실제로 재되, **MPM의 NSDF clarity와 같은 척도가 아니다** —
 * 두 경로의 confidence 숫자를 서로 비교하면 안 된다.
 */
export const lagCorrelation = (window: Float32Array, lag: number): number => {
  const n = window.length - lag;
  if (lag < 1 || n < 2) {
    return 0;
  }
  let dot = 0;
  let energyA = 0;
  let energyB = 0;
  for (let i = 0; i < n; i += 1) {
    const a = window[i];
    const b = window[i + lag];
    dot += a * b;
    energyA += a * a;
    energyB += b * b;
  }
  const denom = Math.sqrt(energyA * energyB);
  if (denom <= 0) {
    return 0;
  }
  return clampNum(dot / denom, 0, 1);
};

export interface MicFrameInput {
  /** 시간영역 표본 한 창(analyser의 `getFloatTimeDomainData` 결과). */
  window: Float32Array;
  sampleRate: number;
  timestampMs: number;
  /** 지금 튜닝 패널이 고른 값 — 유성 게이트가 **실제로** 이 값들을 쓴다. */
  analysis: PitchAnalysisConfig;
}

/**
 * 창 하나를 Analysis Frame으로. 유성 판정은 세 관문을 모두 통과해야 한다 —
 * 음량(silence_threshold_db) · 음역(min/max_frequency_hz) · 신뢰도
 * (confidence_threshold). 네이티브 게이트와 **같은 축**이라 슬라이더를 굴리면 곡선이
 * 실제로 끊기고 이어진다(그게 이 소스의 목적이다).
 */
export const buildMicFrame = (input: MicFrameInput): NativeF0Frame => {
  const { window, sampleRate, timestampMs, analysis } = input;
  const rmsDb = windowRmsDb(window);
  const base = {
    timestampMs,
    rmsDb,
    algorithm: 'acf' as const,
    captureSampleRate: sampleRate,
    analysisSampleRate: sampleRate,
  };
  const unvoiced: NativeF0Frame = {
    ...base,
    f0Hz: null,
    midi: null,
    note: null,
    confidence: 0,
    voiced: false,
  };

  if (rmsDb <= analysis.silenceThresholdDb) {
    return unvoiced;
  }
  const f0 = autoCorrelate(window, sampleRate);
  if (f0 <= 0 || f0 < analysis.minFrequencyHz || f0 > analysis.maxFrequencyHz) {
    return unvoiced;
  }
  const confidence = lagCorrelation(window, Math.round(sampleRate / f0));
  if (confidence < analysis.confidenceThreshold) {
    // 음높이는 나왔지만 신뢰도 미달 — 값은 버리고 confidence는 남긴다(왜 떨어졌는지
    // 리드아웃에서 보여야 임계값을 어디로 옮길지 판단할 수 있다).
    return { ...unvoiced, confidence };
  }
  const note = freqToNote(f0);
  return {
    ...base,
    f0Hz: f0,
    midi: note.midi,
    note: `${note.name}${note.octave}`,
    confidence,
    voiced: true,
  };
};
